import StaffUser from '../model/StaffUser';
import {
  normalizeIdentifier,
  createError,
  castToBoolean,
} from "../util"
import m from './module';

const loginUrl           = '/auth/login';
const logoutUrl          = '/auth/logout';
const currentUserPath    = '/staffing_user/current';
const getClaimsUrl       = '/staff_get_claims';
const getRequestsUrl     = '/staff_get_requests';
const getProfileUrl      = '/staff_get_profile';
const setProfileUrl      = '/staff_set_profile';
const getBannersUrl      = '/staff_banners';
const getAvailabilityUrl = '/staff_get_availability';
const setAvailabilityUrl = '/staff_set_availability';
const acceptPositionUrl  = '/staff_accept_position';
const declinePositionUrl = '/staff_decline_position';
const checkInUrl         = '/staff_checkin';
const editPositionUrl    = '/staff_edit_position';
const createAdhocUrl     = '/create_adhoc';
const createGuerrillaStopUrl = '/create_guerrilla_stop';
const validatePasswordUrl = '/staff_validate_password';
const changePasswordUrl  = '/staff_change_password';
const getFileHref         = '/document_href/';
const changeLanguagePath = '/staffing_user/{{staffing_users_id}}/preferred_language';
const changeDataSyncWindowPath = '/staffing_user/{{staffing_users_id}}/data_sync_window';

class StaffUserService {
  constructor(
    $log,
    $q,
    $injector,
    $timeout,
    $rootScope,
    APIService,
    ApplicationCache,
    DateTimeService,
    LogService,
    LanguageService,
    PositionService,
    EventService,
    PartialResponseService,
    PartialClaimService,
    RecapResponseService,
    PositionSearchService,
    ProgramService,
    VenueService,
    ClientService,
    ClaimService,
    RecapDefinitionService,
    OpportunityInvitationService,
    Base64
  ) {
    this.$log             = $log;
    this.$q               = $q;
    this.$injector        = $injector;
    this.$timeout         = $timeout;
    this.$rootScope       = $rootScope;
    this.APIService       = APIService;
    this.ApplicationCache = ApplicationCache;
    this.DateTimeService  = DateTimeService;
    this.LogService       = LogService;
    this.LanguageService  = LanguageService;
    this.PartialResponseService = PartialResponseService;
    this.PartialClaimService = PartialClaimService;
    this.RecapResponseService = RecapResponseService;
    this.PositionService  = PositionService;
    this.EventService     = EventService;
    this.PositionSearchService = PositionSearchService;
    this.VenueService     = VenueService;
    this.ProgramService   = ProgramService;
    this.Base64           = Base64;

    this.ClientService          = ClientService;
    this.ClaimService           = ClaimService;
    this.RecapDefinitionService = RecapDefinitionService;
    this.OpportunityInvitationService = OpportunityInvitationService;

    this.username = null;
    this.password = null;

    this._messageCount      = 0;
    this._positions         = [];
    this._previousPositions = [];
    this._currentPositions  = [];
    this._upcomingPositions = [];

    this._pendingRequests = [];
    this._confirmedRequests = [];
    this._declinedRequests = [];

    this._profile = {};

    this._stale = true;

    this._refreshPositionsPromise = null;
    this._refreshPositionsAgain = false;
    this._refreshDataPromise = null;
    this._refreshDataAgainPromise = null;

    this._lastRefreshPositionsStartedAt = null;
    this._lastRefreshPositionsCompletedAt = null;
    this._lastRefreshPositionsErrorAt = null;

    this._refreshPositionsInProgress = false;
    this._refreshDataInProgress = false;
    this._loginInProgress = false;
  }

  set currentUser(user) {
    this._currentUser = user;
    this.currentUserId = user && user.id;
  }

  get currentUser() {
    return this._currentUser;
  }

  set currentUserId(id) {
    this._currentUserId = id;
  }

  get currentUserId() {
    return this._currentUserId;
  }

  set stale(stale) {
    this._stale = stale;
  }

  get stale() {
    return this._stale;
  }

  get lastRefreshPositionsStartOrEndDate() {
    let mostRecent = null;
    if (this._lastRefreshPositionsCompletedAt) {
      mostRecent = this._lastRefreshPositionsCompletedAt;
    }
    if (this._lastRefreshPositionsStartedAt && this._lastRefreshPositionsStartedAt > mostRecent) {
      mostRecent = this._lastRefreshPositionsStartedAt;
    }
    return mostRecent;
  }

  get isRefreshInProgress() {
    return this._refreshPositionsInProgress || this._refreshDataInProgress || this._loginInProgress;
  }

  get refreshDataPromise() {
    return this._refreshDataPromise;
  }

  get refreshPositionsPromise() {
    return this._refreshPositionsAgain;
  }

  create(data, config = null) {
    const promises = [];
    data = data || {};

    // Populate clients
    if (data.clients) {
      promises.push(
        this.$q.all(data.clients.map(clientData => this.ClientService.create(clientData)))
          .then(clients => data.clients = clients)
      )
    }

    // Populate programs
    if (data.programs) {
      promises.push(
        this.$q.all(data.programs.map(programData => this.ProgramService.create(programData)))
          .then(programs => {
            data.programs = programs;
            return programs;
          })
      )
    }

    // Populate venues
    if (data.venues) {
      promises.push(
        this.$q.all(data.venues.map(venueData => this.VenueService.create(venueData)))
          .then(venues => data.venues = venues)
      )
    }

    // Populate languages
    const allowDynamicCreationOfLanguages = false;
    if (data.languages && allowDynamicCreationOfLanguages) {
      // TODO: Determine if this should be enabled in the future
      // (Currently disabled to avoid duplication of languages. Instead, languages are loaded via JSON at build-time)
      promises.push(
        this.LanguageService.createAll(data.languages)
          .then(languages => {
            this.LogService.debug("StaffUserService created languages:", languages);
          })
      )
    }

    // Set preferred language if present
    if (data.preferred_language_id && !this._pendingLanguageChangeLocale) {
      this.LogService.debug(`StaffUserService.create() setting preferred language with id:`, data.preferred_language_id);
      this.LanguageService.setActiveLanguageWithId(data.preferred_language_id);
    }

    // Set sync window from DataSyncService
    promises.push(
      this.$q.when(this.$injector.get('DataSyncService'))
        .then(DataSyncService => {
          if (DataSyncService.syncWindowDefinition) {
            data.syncWindowName = DataSyncService.syncWindowDefinition.name;
          }
        })
    );

    return this.$q.all(promises)
      .then(() => {
        return new StaffUser(data);
      });
  }

  logout() {
    return this.APIService.post(logoutUrl)
      .catch(r => {
        this.LogService.warn(`Error sending logout to server:`, r);
      })
      .then(() => this.APIService.clearCredentials())
      .then(() => {
        this.currentUser.clearAllData();
        this.PositionSearchService.clearAllData();
        this.currentUser = null;
        this.currentUserId = null;
        this.username = null;
      })
  }

  /**
   * Log the user in given a username and password
   * @param username
   * @param password
   * @returns {*}
   */
  loginAndCreate(username, password) {
    this._loginInProgress = true;
    this.username = username;
    return this.getLoginTokenUsingCredentials(username, password)
      .then(token => this.loginAndCreateViaToken(token));
  }

  loginAndCreateViaToken(token) {
    const loginRequestConfig = { force: true };
    this._loginInProgress = true;
    return this.$q.when(this.APIService.setAuthToken(token))
      .then(() => this.retrieveLoggedInUserDataFromAPI(loginRequestConfig))
      .catch(loginError => {
        return this.$q.when(this.APIService.clearCredentials())
          .finally(() => {
            return this.$q.reject(loginError);
          });
      })
      .then(loginResponse => this.completeLoginWithResponse(loginResponse))
      .finally(r => {
        this._loginInProgress = false;
        return r;
      });
  }

  getLoginTokenUsingCredentials(username, password) {
    const loginData = {
      username,
      password,
    };
    this.username = username;
    this.APIService.clearCredentials();
    return this.APIService.post(loginUrl, loginData)
      .catch(err => {
        return this.$q.reject(createError({
          error: 'InvalidLogin',
          message: "Invalid login credentials",
        }));
      })
      .then(response => {
        const token = response.data.token;
        return this.$q.when(this.APIService.setAuthToken(token))
          .then(() => token);
      });
  }

  /**
   * Log the user in without changing credentials, presumably already set in APIService
   * @param config
   * @returns {*}
   */
  refreshCurrentLoginSession(config = null) {
    return this.retrieveLoggedInUserDataFromAPI(config)
      .then(function copyCurrentUserLocalDataToLoginResponse(loginResponse) {
        if (loginResponse && loginResponse.data) {
          // Copy app-only metadata from old user to new
          // This fixes a bug where maxDaysOfData was forgotten on each data sync
          // ^ which has now been replaced by StaffUser.syncWindowName
          const localDataFields = [
            'syncWindowName',
          ];
          localDataFields.forEach(field => {
            loginResponse.data[field] = this.currentUser[field];
          })
        } else {
          this.LogService.warn(`Invalid login response:`, loginResponse);
        }
        return loginResponse;
      })
      .then(loginResponse => this.completeLoginWithResponse(loginResponse));
  }

  retrieveLoggedInUserDataFromAPI(config = null) {
    return this.APIService.get(currentUserPath, config)
      .catch(response => {
        const loginError = createError({
          error: 'InvalidLogin',
          message: response && response.data && response.data.error || "Invalid login",
          response,
        });
        return this.$q.reject(loginError);
      });
  }

  completeLoginWithResponse(loginResponse, config = null) {
    return this.$q.when(loginResponse)
      .then(loginResponse => {
        const data = loginResponse && loginResponse.data;
        const userId = data && data.id;
        if (!userId) {
          return this.$q.reject(createError({
            error: 'InvalidLoginResponse',
            message: 'Invalid login response returned from server',
            response: loginResponse,
          }));
        }
        return this.create(data, config)
      })
      .then(user => {
        this.currentUser = user;
        return user;
      })
      .then((user) => {
        this.currentUser = user;
        return user;
      })
      .catch(err => {
        this.LogService.warn(`StaffUserService.completeLoginWithResponse() error creating staff user. Response:`, loginResponse, `create error:`, err);
        return this.$q.reject(err);
      })
      .then(user => {
        if (this._pendingLanguageChangeLocale) {
          return this.changeUserLanguage(user.id, this._pendingLanguageChangeLocale)
            .then(() => user)
            .catch(err => {
              this.LogService.warn(`Swallowing error changing language after login:`, err);
              return user;
            })
        } else {
          return user;
        }
      });
  }

  getById(staffingUserId, config = null) {
    const currentUser = this.currentUser;
    const currentUserId = currentUser && normalizeIdentifier(currentUser.id);
    staffingUserId = normalizeIdentifier(staffingUserId);

    if (!currentUserId) {
      this.LogService.warn(`StaffUserService.getById(${staffingUserId}) - no current user!`);
      return this.$q.reject(createError({
        error: 'UserNotLoggedIn',
        message: 'User not logged in',
      }));
    } else if (currentUserId !== staffingUserId) {
      this.LogService.warn(`RecapResponseService.getStaffUserById(${staffingUserId}) ID does not match currently logged-in user's ID: ${currentUser.id}. Current user:`, currentUser);
      return this.$q.reject(createError({
        error: 'InvalidStaffUserIdForRetrieval',
        message: 'Invalid staff user ID',
      }));
    } else {
      return this.$q.when(currentUser);
    }
  }

  getRequests(config = null) {
    return this.APIService.get(getRequestsUrl, config)
      .then(response => {
        return this.$q.when(response.data);
      })
      .catch(response => {
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }

  getPositionsForStaffingUser(staffingUser, config = null) {
    return this.PositionService.getAllForStaffingUser(staffingUser, config)
  }

  getPartialsForStaffingUser(staffingUser, config = null) {
    return this.PartialResponseService.getAllForStaffingUser(staffingUser, config);
  }

  getPartialClaimsForStaffingUser(staffingUser, config = null) {
    return this.PartialClaimService.getAllForStaffingUser(staffingUser, config);
  }

  getClaimsForStaffingUser(staffingUser, config = null) {
    return this.ClaimService.getAllForStaffUser(staffingUser, config)
      .catch(response => {
        this.LogService.error("Error response getting claims:", response);
        return this.$q.reject(createError({
          error: 'ErrorRetrievingClaims',
          message: response.data && response.data.error || "Error retrieving claims",
          response,
        }));
      });
  }

  getProfile(config = null) {
    return this.APIService.get(getProfileUrl, config)
      .then(response => {
        let data = response.data;

        // Populate multi-language text for custom fields
        let customFieldsData = data.custom_fields || [];
        const customFieldMultiLanguageFields = ['name', 'notes'];
        const customFieldOptionMultiLanguageFields = ['name'];
        data.custom_fields = customFieldsData.map(customFieldData => {
          const augmentedFieldData = this.LanguageService.populateMultiLanguageFields(customFieldData, customFieldMultiLanguageFields);
          if (augmentedFieldData.options) {
            const augmentedOptions = augmentedFieldData.options.map(optionData => {
              return this.LanguageService.populateMultiLanguageFields(optionData, customFieldOptionMultiLanguageFields);
            });
            return {
              ...augmentedFieldData,
              options: augmentedOptions,
            };
          } else {
            return augmentedFieldData;
          }
        });
        return data;
      })
      .catch(response => {
        this.$log.info("Error getting staff profile. Response: ", response);
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }

  getAvailability(month, year, config = null) {
    let myConfig = {
      ...config,
      params: {
        month,
        year,
      },
    };
    return this.APIService.get(getAvailabilityUrl, myConfig)
      .then( response => {
        return this.$q.when(response.data);
      })
      .catch(response => {
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }

  /**
   * Set staff user availability
   * @param position position
   */
  declinePosition(position) {
    let params = {
      id: position.id,
    };

    return this.APIService.post(declinePositionUrl, params)
      .then( response => {
        this.stale = true;
        return this.$q.when(response.data);
      })
      .catch(response => {
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }
  /**
   * Set staff user availability
   * @param obj position
   */
  acceptPosition( position ) {
    let params = {
      id: position.id,
    };

    return this.APIService.post(acceptPositionUrl, params)
      .then( response => {
        this.stale = true;
        return this.$q.when(response.data);
      })
      .catch(response => {
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }


  /**
   * Set staff user availability
   * @returns {*|Promise.<T>}
   * @param date
   * @param available
   */
  setAvailability(date, available) {
    let params = {
      date: date,
      available: castToBoolean(available) ? 't' : 'f',
    };

    return this.APIService.post(setAvailabilityUrl, params)
      .then( response => {
        this.stale = true;
        return this.$q.when(response.data);
      })
      .catch(response => {
        this.$log.info("Error getting staff availability. Response: ", response);
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }

  validatePassword(password) {
    let params = {
      password: password
    }

    return this.APIService.post(validatePasswordUrl, params)
      .then( response => {
        return this.$q.when(response.data);
      });
  }

  changePassword(oldPassword, newPassword, newPasswordConfirm) {
    const params = {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    };
    return this.APIService.post(changePasswordUrl, params)
      .catch(response => {
        this.LogService.error("StaffUserService.changePassword() got error:", response);
        return this.$q.reject(response.data);
      })
      .then(response => {
        return response.data;
      })
      .then(changePasswordResponseData => {
        // Update credentials
        return this.getLoginTokenUsingCredentials(this.username, newPassword)
          .then(() => changePasswordResponseData);
      })
  }

  setProfile(profile) {
    let params = {
      profile: profile,
    };
    return this.APIService.post(setProfileUrl, params)
      .catch(response => {
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }

  getBanners() {
    return this.APIService.get(getBannersUrl, {disableCache: true})
      .then( response => {
        return this.$q.when(response.data);
      })
      .catch(response => {
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }

  checkIn( eventId, type, timestamp, lat, lon) {
    let params = {
      position: {real_events_id: eventId},
      type: type,
      timestamp: timestamp,
      lat: lat,
      lon: lon,
    };

    return this.APIService.post(checkInUrl, params)
      .then( response => {
        this.stale = true;
        return this.$q.when(response.data);
      })
      .catch(response => {
        return this.$q.reject(createError({
          error: 'InvalidResponse',
          message: response.data.error,
          response
        }));
      });
  }

  refreshData(staffUser, config = null) {
    config = config || {};

    this._refreshDataInProgress = true;

    // Force refresh by default
    if (!('force' in config)) {
      config.force = true;
    }
    config.maxDaysOfData = staffUser.maxDaysOfData;

    if (!staffUser) {
      this.LogService.warn("StaffUserService.refreshData() called but no staffUser provided. Setting to currentUser:", this.currentUser);
      staffUser = this.currentUser;
    }

    staffUser.previousPositions = [];
    staffUser.currentPositions  = [];
    staffUser.upcomingPositions = [];
    staffUser.pendingRequests   = [];
    staffUser.confirmedRequests = [];
    staffUser.declinedRequests  = [];
    staffUser.recapsDue         = [];
    staffUser.opportunityInvitations = [];
    staffUser.opportunityInvitationsByGroup = {};
    staffUser.events = [];

    const ensureEventsLoadFirstPromise = this.$q.when(null)
      .then(() => {
        // First load events
        return this.EventService.getAllForStaffUserId(staffUser.id, config)
          .then(events => {
            this.LogService.debug(`StaffUserService.refreshData() setting staffUser.events with ${events.length} events`);
            staffUser.events = events;
            return events;
          });
      })
      .then(events => {
        // MJOL-1611 Also re-fetch staff user and selectively update things (like programs)
        return this.retrieveLoggedInUserDataFromAPI({force: true})
          .then(userDataResponse => {
            if (!userDataResponse) {
              return this.$q.reject(createError({name: 'MissingLoginResponse'}));
            }
            return userDataResponse.data;
          })
          .then(userData => {
            return this.$q.all(userData.programs.map(programData => this.ProgramService.create(programData)))
              .then(programs => {
                staffUser.programs = programs;
              })
          })
          .then(() => events);
      })
      .then(events => {
        const promises = [];
        promises.push(this.RecapResponseService.getAllForStaffUser(staffUser, config));
        promises.push(this.refreshPositions(staffUser, config));
        promises.push(
          this.getProfile(config)
            .then(response => {
              staffUser.profile = response;
            })
        );
        promises.push(
          this.getBanners()
            .then(response => {
              staffUser.banners = response;
            })
        );
        promises.push(
          this.getAvailability(undefined, undefined, config)
            .then(response => {
              staffUser.unavailableDates = response.unavailableDates;
            })
        );
        promises.push(
          this.refreshOpportunityInvitations(staffUser, config)
        );
        return this.$q.all(promises);
      })

    this._refreshDataPromise = ensureEventsLoadFirstPromise
      .then(() => {
        this.stale = false;
        return staffUser;
      })
      .finally(r => {
        this._refreshDataInProgress = false;
        return r;
      });
    return this._refreshDataPromise;
  }

  refreshOpportunityInvitations(staffUser, config = null) {
    return this.OpportunityInvitationService.getAllForStaffingUser(staffUser, config)
      .then(opportunityInvitations => {
        staffUser.opportunityInvitations = opportunityInvitations;
        staffUser.opportunityInvitationsByGroup = this.OpportunityInvitationService.groupOpportunityInvitationsByResponse(opportunityInvitations);
      })
      .catch(err => {
        this.LogService.warn(`StaffUserService.refreshOpportunityInvitations error:`, err);
        return this.$q.reject(err);
      })
  }

  // Used after an adhoc event is created, refreshes position data only
  refreshPositions(staffUser, config = null) {
    const promises = [];
    this._refreshPositionsInProgress = true;

    if (!staffUser) {
      this.LogService.warn("StaffUserService.refreshPositions() called but no staffUser provided. Setting to currentUser:", this.currentUser);
      staffUser = this.currentUser;
    }

    this._lastRefreshPositionsStartedAt = new Date();

    const removeCanceledEventsFromPositionList = positions => {
      // Filter out canceled events
      const uncanceledPositions = positions.filter(position => !position.event.isCancelled());
      const numCanceled = positions.length - uncanceledPositions.length;
      return uncanceledPositions;
    };

    promises.push(
      this.getPositionsForStaffingUser(staffUser, config)
        .then(positions => {
          staffUser.positions = positions;
        })
    );
    promises.push(
      this.getPartialsForStaffingUser(staffUser, config)
        .then(partials => {
          staffUser.partialRecaps = partials;
        })
    );
    promises.push(
      this.getPartialClaimsForStaffingUser(staffUser, config)
        .then(partials => {
          staffUser.partialClaims = partials;
        })
    )
    promises.push(
      this.getRequests(config)
        .then(response => {
          staffUser.pendingRequests   = response.pending_requests;
          staffUser.confirmedRequests = response.confirmed_requests;
          staffUser.declinedRequests  = response.declined_requests;
        })
    );
    promises.push(
      this.getClaimsForStaffingUser(staffUser, config)
        .then(claims => {
          staffUser.claims = claims;
        })
    );

    this._refreshPositionsPromise = this.$q.all(promises)
      .then(() => this.preloadRecapDefinitionsForUser(staffUser, config))
      .then(resp => {
        this._lastRefreshPositionsErrorAt = null;
        this._lastRefreshPositionsCompletedAt = new Date();
        return resp;
      })
      .then(resp => {
        // Set PositionSearchService.allPositions after recap definitions are preloaded
        this.PositionSearchService.allPositions = removeCanceledEventsFromPositionList(staffUser.positions);
        return resp;
      })
      .then(resp => {
        this.$rootScope.$broadcast('StaffUserService.refreshPositionsComplete');
        return resp;
      })
      .finally(r => {
        this._refreshPositionsInProgress = false;
        return r;
      });
    return this._refreshPositionsPromise;
  }

  preloadRecapDefinitionsForUser(staffUser, config = null) {
    const recapDefinitionPromiseMap = staffUser.positions.reduce((promiseMap, position) => {
      const event = position.event || null;
      const recapDefinitionId = event && event.events_recaps_id;
      if (recapDefinitionId && promiseMap[recapDefinitionId] === undefined) {
        promiseMap[recapDefinitionId] = this.RecapDefinitionService.getById(recapDefinitionId, config);
      }
      return promiseMap;
    }, []);

    return this.$q.all(Object.values(recapDefinitionPromiseMap))
      .then(recapDefinitions => {
        const recapDefinitionsById = recapDefinitions.reduce((recapDefinitionsById, recapDefinition) => {
          recapDefinitionsById[recapDefinition.id] = recapDefinition;
          return recapDefinitionsById;
        }, {});

        // Ensure that recap definition is set for all events
        staffUser.positions.forEach(position => {
          const event = position.event;
          if (event && event.events_recaps_id && !event.recapDefinition) {
            const recapDefinition = recapDefinitionsById[event.events_recaps_id];
            if (recapDefinition) {
              event.recapDefinition = recapDefinition;
            }
          }
        });

        return recapDefinitions;
      });
  }

  saveAdhoc(data) {
    return this.APIService.post(createAdhocUrl, data);
  }

  saveGuerrillaStop(data) {
    return this.APIService.post(createGuerrillaStopUrl, data);
  }

  getFile(id) {
    let config = {
      force: true
    };
    return this.APIService.get(getFileHref + id, config);
  }

  changeUserLanguage(staffingUsersId, locale) {
    const config = { force: true };
    const url = changeLanguagePath.replace('{{staffing_users_id}}', staffingUsersId);
    const data = { locale };
    return this.APIService.post(url, data);
  }

  changeUserLanguageOnNextLogin(locale) {
    this._pendingLanguageChangeLocale = locale;
  }

  changeUserDataSyncWindow(staffingUsersId, dataSyncWindowName) {
    const config = { force: true };
    const url = changeDataSyncWindowPath.replace('{{staffing_users_id}}', staffingUsersId);
    const data = { data_sync_window_name: dataSyncWindowName };
    return this.APIService.post(url, data);
  }
}

m.service('StaffUserService', StaffUserService);
